"use client";

import { Badge } from "@/components/badge";
import { cx } from "@/components/cx";
import {
  BASEMAPS,
  BASEMAP_LABELS,
  isPaidBasemap,
  normalizeBasemap,
  type Basemap,
  type MapSpec,
} from "@/lib/map";

export interface BasemapPanelProps {
  spec: MapSpec;
  /** Whether publishing the selected paid basemap is already entitled. */
  isPaid: boolean;
  /** Commit a basemap choice — writes `spec.basemap`. */
  onSpecChange: (next: MapSpec) => void;
}

/**
 * The "Maps" tab (MAP-102 follow-up). The basemap picker as a scannable tile
 * grid — lifted out of the Style tab's dropdown so every style is visible at a
 * glance, each with a bundled thumbnail. Satellite remains selectable while
 * editing; Free users see its Lite badge and are gated only when publishing.
 *
 * The tile is a raw radio `<button>` (like the Style tab's marker picker), not
 * the Button primitive: it's a selectable swatch, not a CTA. Its class carries
 * no `btn`/`button`/`cta` word so the button-primitive lint never claims it.
 */
export function BasemapPanel({ spec, isPaid, onSpecChange }: BasemapPanelProps) {
  const current = normalizeBasemap(spec.basemap);

  function choose(basemap: Basemap) {
    if (basemap === current) return;
    onSpecChange({ ...spec, basemap });
  }

  return (
    <div className="basemap-grid" role="radiogroup" aria-label="Basemap">
      {BASEMAPS.map((basemap) => {
        const active = basemap === current;
        const requiresLite = !isPaid && isPaidBasemap(basemap);
        return (
          <button
            key={basemap}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={requiresLite ? `${BASEMAP_LABELS[basemap]} (requires Lite to publish)` : BASEMAP_LABELS[basemap]}
            className={cx(
              "basemap-tile",
              active && "basemap-tile--active",
            )}
            onClick={() => choose(basemap)}
          >
            <span className={cx("basemap-tile__thumb", `basemap-tile__thumb--${basemap}`)} aria-hidden />
            <span className="basemap-tile__meta">
              <span className="basemap-tile__label">{BASEMAP_LABELS[basemap]}</span>
              {requiresLite && (
                <Badge variant="accent" className="basemap-tile__badge">
                  Lite
                </Badge>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}
